import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "../api/axiosConfig";
import ZonalHeadDashboard from "./ZonalHeadDashboard";
import SalesTracker from "./SalesTracker";
import {
  CircularProgress,
  Box,
  Typography,
  Alert,
  Button,
} from "@mui/material";

const ZonalHeadDashboardWrapper = () => {
  const navigate = useNavigate();
  const { section, "*": restOfUrl } = useParams();
  const currentSection = section || "dashboard";

  let targetUserId = "me";
  let entryDateForTracker = new Date().toISOString().split("T")[0];

  if (section === "input-for" && restOfUrl) {
    const parts = restOfUrl.split("/");
    targetUserId = parts[0];
    if (parts[1]) entryDateForTracker = parts[1];
  } else if (section === "input" && restOfUrl) {
    entryDateForTracker = restOfUrl;
  }

  const [dashboardData, setDashboardData] = useState(null);
  const [teamPerformanceData, setTeamPerformanceData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [myPerformanceRange, setMyPerformanceRange] = useState(30);
  const [teamPerformanceRange, setTeamPerformanceRange] = useState(30);

  const token = localStorage.getItem("userToken");

  useEffect(() => {
    if (!token) {
      navigate("/user-login");
    }
  }, [token, navigate]);

  const fetchAllDashboardData = useCallback(() => {
    if (!token) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    const personalDashboardPromise = api.get(
      `/api/users/me/dashboard?range=${myPerformanceRange}`
    );
    const teamPerformancePromise = api.get(
      `/api/users/branch-managers-by-zonal-head?range=${teamPerformanceRange}`
    );

    Promise.all([personalDashboardPromise, teamPerformancePromise])
      .then(([personalRes, teamRes]) => {
        setDashboardData(personalRes.data);
        setTeamPerformanceData(teamRes.data);
      })
      .catch((err) => {
        console.error("Failed to fetch dashboard data:", err);
        setError(
          `Failed to load data: ${err.response?.data?.message || err.message}`
        );
      })
      .finally(() => {
        setLoading(false);
      });
  }, [token, myPerformanceRange, teamPerformanceRange]);

  useEffect(() => {
    fetchAllDashboardData();
  }, [fetchAllDashboardData]);

  const handleNavigateToEntry = (date) => {
    const dateStr = date || new Date().toISOString().split("T")[0];
    navigate(`/zonal-head-dashboard/input/${dateStr}`);
  };

  const handleEntrySubmitted = () => {
    fetchAllDashboardData();
    navigate("/zonal-head-dashboard/dashboard");
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Loading Dashboard...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 4, textAlign: "center" }}>
        <Alert severity="error">{error}</Alert>
        <Button
          onClick={fetchAllDashboardData}
          sx={{ mt: 2 }}
          variant="outlined"
        >
          Try Again
        </Button>
      </Box>
    );
  }

  if (currentSection === "input" || currentSection === "input-for") {
    return (
      <SalesTracker
        dashboardData={dashboardData}
        onBackClick={() => navigate("/zonal-head-dashboard/dashboard")}
        onEntrySubmit={handleEntrySubmitted}
        userId={targetUserId}
        entryDate={entryDateForTracker}
        onEntryUpdate={(message, date) => {
          // Handle entry update notification if needed
          console.log(`Entry updated: ${message} on ${date}`);
        }}
      />
    );
  }

  return (
    <ZonalHeadDashboard
      dashboardData={dashboardData}
      teamPerformanceData={teamPerformanceData}
      onQuickEntryClick={() => handleNavigateToEntry()}
      onDateClick={handleNavigateToEntry}
      myPerformanceRange={myPerformanceRange}
      setMyPerformanceRange={setMyPerformanceRange}
      teamPerformanceRange={teamPerformanceRange}
      setTeamPerformanceRange={setTeamPerformanceRange}
    />
  );
};

export default ZonalHeadDashboardWrapper;
